import { createSelector, createStructuredSelector } from 'reselect';
import { filter, flow, sortBy } from 'lodash';
import { List } from 'immutable';
import { REPORT_YEAR } from './constants';
import { selectNDFL3 } from 'modules/Taps/components/NDFL3/selectors';
import moment from 'utils/moment';
import { checks } from 'modules/Taps/components/NDFL3/Ndfl3ChecksSelector';
import { routeMap } from 'router';
import { selectRouterCardId } from 'modules/App/utils/selectors';

const filterZeroSection = (sectionOne) =>
  sectionOne.filter((section) => section.get('podlUpl') || section.get('podlVozv'));
const hrefPaymentCreator = (cardId) => ({
  pathname: routeMap.taxesPayment,
  query: {
    taxType: 'NDFL3',
    ndfl3Id: cardId,
  },
});

const hrefRefundCreator = (cardId) => ({
  pathname: routeMap.disposeOfOverpayment,
  query: { declarationId: cardId },
});

export const selectNDFL3Light = ({ taps: { ndfl3Light } }) => ndfl3Light;

export const getStepFromRouter = ({
  router: {
    query: { step },
  },
}) => step;

export const getCardIdFromRouter = ({
  router: {
    query: { cardId },
  },
}) => parseInt(cardId) || null;

export const selectInitialized = createSelector(selectNDFL3Light, (state) =>
  state.get('initialized'),
);

export const selectYear = createSelector(
  selectNDFL3Light,
  (state) => state.getIn('draft.payload.initialData.reportYear'.split('.')) || REPORT_YEAR,
);

export const selectNDFL3Phone = createSelector(
  selectNDFL3Light,
  (state) => state.getIn('draft.payload.initialData.phone'.split('.'))
);
export const selectDraftMode = createSelector(selectNDFL3Light, (state) => state.get('mode'));

export const selectParentDeclaration = createSelector(selectNDFL3Light, (state) =>
  state.get('parentDeclaration'),
);

export const selectDraft = createSelector(selectNDFL3Light, (state) => state.get('draft'));

export const selectNdfl2 = createSelector(selectNDFL3Light, (state) => state.get('ndfl2'));

export const selectXmlDraft = createSelector(selectNDFL3Light, (state) => state.get('draftXml'));

export const selectSectionOne = createSelector(selectDraft, (draft) =>
  draft ? draft.getIn(['payload', 'sectionOne']) : List(),
);

export const selectSumToReturn = createSelector(selectSectionOne, (sectionOne) =>
  sectionOne
    ? sectionOne.map((value) => value.get('podlVozv')).reduce((prev, value) => prev + value, 0)
    : null,
);

export const selectSumToPay = createSelector(selectSectionOne, (sectionOne) =>
  sectionOne
    ? sectionOne.map((value) => value.get('podlUpl')).reduce((prev, value) => prev + value, 0)
    : null,
);

export const selectDeclarationSubmitted = createSelector(selectNDFL3Light, (state) =>
  state.get('declarationSubmitted'),
);

export const selectAllowedFileCategories = createSelector(
  selectDraft,
  (draft) => draft.getIn(['payload', 'allowedFileCategories']) || List(),
);

export const selectFileCategories = createSelector(
  selectNDFL3,
  selectAllowedFileCategories,
  ({ dictionaries: { fileCategories: { data = [] } = {} } }, allowedFileCategories) =>
    sortBy(
      data.filter(({ docType }) => allowedFileCategories.includes(docType)),
      ['sortOrder', 'docType', 'documentDescription'],
    ),
);

export const selectDraftError = createSelector(selectNDFL3Light, (state) => state.get('error'));

export const selectChecks = createSelector(checks, selectYear, (checks, reportYear) =>
  filter(checks, (check) => moment(check.originDate).year() === reportYear),
);

export const selectDraftSectionOne = createSelector(selectDraft, (draft) =>
  draft.getIn(['payload', 'sectionOne']),
);

export const showLinksSelector = createStructuredSelector({
  linkRefund: flow(
    selectDraft,
    (draft) =>
      !!(
        draft.getIn(['payload', 'initialData', 'reportYear']) < 2020 &&
        draft.getIn(['payload', 'sectionOne']) &&
        filterZeroSection(draft.getIn(['payload', 'sectionOne'])).find(
          (el) => el.get('prUplVozv') === '2',
        )
      ),
  ),
  linkPayment: flow(
    selectDraftSectionOne,
    (sectionOne) =>
      !!(sectionOne && filterZeroSection(sectionOne).find((el) => el.get('prUplVozv') === '1')),
  ),
});

export const hrefResultSelector = createSelector(selectRouterCardId, (cardId) => {
  return {
    hrefRefund: hrefRefundCreator(cardId),
    hrefPayment: hrefPaymentCreator(cardId),
  };
});

export const selectSavedDraftId = createSelector(selectNDFL3Light, (state) =>
  state.get('savedDraft'),
);

export const selectDraftChecks = createSelector(
  selectDraft,
  (draft) => (draft && draft.getIn(['payload', 'checks'])) || List(),
);

export const selectStepFetching = createSelector(selectNDFL3Light, (state) =>
  state.get('stepFetching'),
);

export const selectDraftAccountDetails = createSelector(selectDraft, (state) =>
  state.getIn(['payload', 'useReturnClaim', 'accountDetails']),
);

export const selectDraftTaxInspectionCode = createSelector(selectDraft, (state) =>
  state.get('taxInspectionCode'),
);
